import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Building2, Hammer, Droplets, ClipboardCheck, FileText, Package, Wrench, TrendingUp, Users, CheckCircle } from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: 'building',
      icon: Building2,
      image: '/images/building-constraction.jpeg',
      title: 'Building & Construction',
      description: 'We deliver building projects across commercial, residential, industrial and specialized development environments, with emphasis on quality workmanship, coordination and reliable project execution.',
      areas: [
        'Commercial Buildings',
        'Residential Developments',
        'Industrial Facilities',
        'Institutional Buildings',
        'Renovation & Refurbishment',
        'Specialized Facilities'
      ]
    },
    {
      id: 'civil',
      icon: Hammer,
      image: '/images/Civil-raod.jpeg',
      title: 'Civil & Road Infrastructure',
      description: 'Our civil works capability covers road and infrastructure development, including earthworks, road improvements, grading, gravelling and associated construction activities.',
      areas: [
        'Road Construction',
        'Earthworks',
        'Road Upgrading',
        'Grading & Gravelling',
        'Infrastructure Works',
        'Drainage Systems'
      ]
    },
    {
      id: 'water',
      icon: Droplets,
      image: '/images/water-infrastructure.jpeg',
      title: 'Water & Infrastructure',
      description: 'We deliver water infrastructure solutions supporting communities, institutions and development projects.',
      areas: [
        'Borehole Projects',
        'Dam Construction',
        'Dam Expansion and Desiltation',
        'Water Towers',
        'Water Storage Systems',
        'Water Collection Points',
        'Water Infrastructure Development'
      ]
    },
    {
      id: 'project',
      icon: ClipboardCheck,
      image: '/images/project-management.jpeg',
      title: 'Project Management',
      description: 'We provide structured project management services from pre-construction through completion, coordinating the various professionals, contractors and stakeholders involved in project delivery.',
      areas: [
        'Project Planning',
        'Stakeholder Coordination',
        'Contractor Coordination',
        'Project Monitoring',
        'Cost and Time Coordination',
        'Quality Oversight',
        'Project Reporting',
        'Completion and Handover Coordination'
      ]
    },
    {
      id: 'construction-management',
      icon: Users,
      image: '/images/construction-management.jpeg',
      title: 'Construction Management',
      description: 'We provide overall coordination and management of construction activities, helping clients maintain control over project cost, time, quality and execution.',
      areas: [
        'Plan',
        'Coordinate',
        'Execute',
        'Monitor',
        'Complete'
      ]
    },
    {
      id: 'design',
      icon: Wrench,
      image: '/images/design-management.jpeg',
      title: 'Design Management',
      description: 'We coordinate the design process and support clients in bringing together the professional expertise required to develop practical, coordinated and construction-ready designs.',
      areas: [
        'Architectural Design',
        'Structural Engineering',
        'Electrical Engineering',
        'Mechanical Engineering',
        'Specialist Consultants'
      ]
    },
    {
      id: 'tender',
      icon: TrendingUp,
      image: '/images/tender-management.jpeg',
      title: 'Tender Management',
      description: 'We support clients through the tendering process by helping identify qualified contractors and suppliers, coordinating tender documentation, evaluating submissions and supporting informed procurement decisions.',
      areas: [
        'Identify',
        'Tender',
        'Evaluate',
        'Recommend',
        'Contract'
      ]
    },
    {
      id: 'consultancy',
      icon: FileText,
      image: '/images/consultancy-services.jpeg',
      title: 'Consultancy Services',
      description: 'Our consultancy services provide clients, designers and contractors with practical technical and project advice throughout the construction process.',
      areas: [
        'Value Engineering & Design Review',
        'Cost Analysis & Estimation',
        'Procurement',
        'Claims Management',
        'Project Troubleshooting'
      ]
    },
    {
      id: 'supplies',
      icon: Package,
      image: '/images/supplies-procurement.jpeg',
      title: 'Supplies & Procurement',
      description: 'We support clients with procurement and supply solutions, including sourcing, evaluation and delivery of required materials, equipment and specialized supplies.',
      areas: [
        'Material Sourcing',
        'Equipment Supply',
        'Supplier Evaluation',
        'Quality Assurance',
        'Delivery Coordination',
        'Specialized Supplies'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Our Services - BURALE Maintainance & Suppliers | Construction Solutions</title>
        <meta name="description" content="Explore our comprehensive construction services: Building & Construction, Civil Works, Water Infrastructure, Project Management, Consultancy and Supplies." />
      </Helmet>
      
      <section className="relative h-96 flex items-center justify-center bg-gradient-to-br from-burale-dark to-burale-teal">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">OUR SERVICES</h1>
          <p className="text-2xl text-burale-gold font-semibold mb-4">Integrated Construction & Project Solutions</p>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            From planning and design coordination to construction, infrastructure, consultancy and supplies, we provide solutions across the project lifecycle.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div key={service.id} id={service.id} className="scroll-mt-24">
                  <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                    <div className="flex-1">
                      <div className="inline-flex items-center justify-center w-20 h-20 bg-burale-teal/10 rounded-xl mb-6">
                        <Icon className="text-burale-teal" size={48} />
                      </div>
                      <h2 className="text-4xl font-bold text-burale-dark mb-6">{service.title}</h2>
                      <p className="text-lg text-gray-700 mb-8 leading-relaxed">{service.description}</p>
                      
                      {service.areas && service.areas.length > 0 && (
                        <div>
                          <h3 className="text-xl font-semibold text-burale-dark mb-4">
                            {service.id === 'building' ? 'Areas include' : 
                             service.id === 'civil' ? 'Focus areas' : 
                             service.id === 'water' ? 'Capabilities' : 
                             service.id === 'project' ? 'Our role can include' : 
                             service.id === 'construction-management' ? 'Our approach' : 
                             service.id === 'design' ? 'We coordinate areas such as' : 
                             service.id === 'tender' ? 'Process' : 
                             service.id === 'consultancy' ? 'Areas' : 
                             'Services'}
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {service.areas.map((area, idx) => (
                              <div key={idx} className="flex items-start space-x-2">
                                <CheckCircle className="text-burale-teal flex-shrink-0 mt-1" size={20} />
                                <span className="text-gray-700">{area}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      {service.image ? (
                        <div className="rounded-2xl h-96 overflow-hidden shadow-2xl">
                          <img 
                            src={service.image} 
                            alt={service.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      ) : (
                        <div className="bg-gradient-to-br from-burale-teal to-burale-dark rounded-2xl p-12 h-96 flex items-center justify-center shadow-2xl">
                          <Icon className="text-white/20" size={200} />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {index < services.length - 1 && (
                    <div className="mt-20 border-b border-gray-200"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-burale-teal text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl mb-8 leading-relaxed">
            Let's discuss how our services can help bring your vision to life.
          </p>
          <a href="/contact" className="bg-white text-burale-teal px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 inline-block text-lg">
            Contact Us Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;
